import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// --- Context & Auth ---
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

import LoginPage from './pages/LoginPage';

// --- Layout ---
import Layout from './components/Layout';

// --- Server Pages ---
import ServersPage from './pages/ServersPage';
import ServerDetailsPage from './pages/ServerDetailsPage';
import ServerStatusHistoryPage from './pages/ServerStatusHistoryPage';
import MedicationHubPage from './pages/MedicationHubPage';
import ServerLoginHistoryPage from './pages/ServerLoginHistoryPage';
import DeviceAccessDetailsPage from './pages/DeviceAccessDetailsPage';
import BroadcastHubPage from './pages/BroadcastHubPage';

// --- Admin Pages ---
import AdminListPage from './pages/AdminListPage';
import AdminDetailsPage from './pages/AdminDetailsPage';
import AdminGeotaggingPage from './pages/AdminGeotaggingPage';

// --- Employee Pages ---
import EmployeeListPage from './pages/EmployeeListPage';
import EmployeeDetailsPage from './pages/EmployeeDetailsPage';
import ManageWorkDaysPage from './pages/ManageWorkDaysPage';

// --- Service User Pages ---
import ServiceUserListPage from './pages/ServiceUserListPage';
import ServiceUserDetailsPage from './pages/ServiceUserDetailsPage';

// --- Operational Pages (Visits) ---
import VisitListPage from './pages/VisitListPage';
import VisitDetailsPage from './pages/VisitDetailsPage';

// --- Live Operations Pages ---
import VisitAlertsPage from './pages/VisitAlertsPage';
import EmergencyAlertsPage from './pages/EmergencyAlertsPage';
import NotificationsPage from './pages/NotificationsPage';

// --- Analytics Pages ---
import BusinessDevelopmentPage from './pages/BusinessDevelopmentPage';
import AnalyticsDetailPage from './pages/AnalyticsDetailPage';

// --- Global Pages ---
import GlobalUsersPage from './pages/GlobalUsersPage';
import RequestedOtpsPage from './pages/RequestedOtpsPage';
import ArchivedServersPage from './pages/ArchivedServersPage';
import GlobalChatPage from './pages/GlobalChatPage';
import GlobalDashboardPage from './pages/GlobalDashboardPage';
import GlobalDirectoryPage from './pages/GlobalDirectoryPage';
import LatestAlertsPage from './pages/LatestAlertsPage';
import LatestBroadcastsPage from './pages/LatestBroadcastsPage';
import AuditLogsPage from './pages/AuditLogsPage';

// --- Support Pages ---
import SupportDashboardPage from './pages/SupportDashboardPage';
import PublicSupportPortal from './pages/PublicSupportPortal';
import PublicTicketTracker from './pages/PublicTicketTracker';
import PublicFeedbackPage from './pages/PublicFeedbackPage';
import SupportStatsPage from './pages/SupportStatsPage';
import SupportMetricDetail from './pages/SupportMetricDetail';

function AppRoutes() {
  const { currentUser, userRole, dynamicServerId, logout } = useAuth(); 

  // Wait for Firebase to figure out who the user is
  if (currentUser !== null && userRole === null && dynamicServerId === null) {
      return (
          <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <h2>Connecting to Dashboard...</h2>
              <small>Verifying permissions...</small>
              
              <div style={{ marginTop: '40px', textAlign: 'center', padding: '20px', backgroundColor: '#fff5f5', borderRadius: '8px', border: '1px solid #fed7d7' }}>
                  <p style={{ color: '#c53030' }}>Taking too long?</p>
                  <p style={{ fontSize: '13px', color: '#718096' }}>You may not have an active Admin profile.</p>
                  <button onClick={logout} style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Logout</button>
              </div>
          </div>
      );
  }

  // ROOT REDIRECT CALCULATOR
  const getRootRedirect = () => {
      if (!currentUser) return "/login";
      if (userRole === 'Dev') return "/dashboard";
      if (userRole === 'Support') return "/support"; 
      if (dynamicServerId) return `/servers/${dynamicServerId}`;
      return "/login";
  };

  return (
    <Routes>
      {/* PUBLIC ROUTES (No Layout, No Authentication) */}
      <Route path="/login" element={!currentUser ? <LoginPage /> : <Navigate to={getRootRedirect()} />} />
      
      {/* INTERNAL ROUTES (Wrapped in Layout Shell) */}
      <Route path="/*" element={
        !currentUser ? <Navigate to="/login" /> : (
          <Layout>
            <Routes>
              {/* ROOT */}
              <Route path="/" element={<Navigate to={getRootRedirect()} />} />

              {/* --- GLOBAL PAGES --- */}
              <Route path="/dashboard" element={<ProtectedRoute requiredRoles={['Dev', 'Support']}><GlobalDashboardPage /></ProtectedRoute>} />
              <Route path="/servers" element={<ProtectedRoute requireGlobal={true}><ServersPage /></ProtectedRoute>} />
              <Route path="/global-chat" element={<ProtectedRoute requireGlobal={true}><GlobalChatPage /></ProtectedRoute>} />
              <Route path="/archived-servers" element={<ProtectedRoute requiredRoles={['Dev']}><ArchivedServersPage /></ProtectedRoute>} />
              <Route path="/requested-otps" element={<ProtectedRoute requiredRoles={['Dev']}><RequestedOtpsPage /></ProtectedRoute>} />
              
              <Route path="/global-directory" element={<GlobalDirectoryPage />} />
              <Route path="/latest-alerts" element={<LatestAlertsPage />} />
              <Route path="/latest-broadcasts" element={<LatestBroadcastsPage />} />
              <Route path="/support" element={<ProtectedRoute><SupportDashboardPage /></ProtectedRoute>} />
              <Route path="/admin/support-analytics" element={<ProtectedRoute requiredRoles={['Dev', 'Support']}><SupportStatsPage /></ProtectedRoute>} />
              <Route path="/admin/support/metrics-detail" element={<SupportMetricDetail />} />

              {/* --- SERVER SPECIFIC ROUTES --- */}
              <Route path="/servers/:serverId" element={<ServerDetailsPage />} />
              <Route path="/servers/:serverId/broadcasts" element={<BroadcastHubPage />} />
              <Route path="/servers/:serverId/medications" element={<MedicationHubPage />} />
              <Route path="/servers/:serverId/admins" element={<AdminListPage />} />
              <Route path="/servers/:serverId/admins/:adminId" element={<AdminDetailsPage />} />
              <Route path="/servers/:serverId/admins/:adminId/geotagging" element={<AdminGeotaggingPage />} />
              
              <Route path="/servers/:serverId/employees" element={<EmployeeListPage />} />
              <Route path="/servers/:serverId/employees/:employeeId" element={<EmployeeDetailsPage />} />
              <Route path="/servers/:serverId/employees/:employeeId/manage-work-days" element={<ManageWorkDaysPage />} />
              
              <Route path="/servers/:serverId/service-users" element={<ServiceUserListPage />} />
              <Route path="/servers/:serverId/service-users/:serviceUserId" element={<ServiceUserDetailsPage />} />

              <Route path="/servers/:serverId/login-history" element={<ServerLoginHistoryPage />} />
              <Route path="/servers/:serverId/login-history/:role/:userId" element={<DeviceAccessDetailsPage />} />
              
              <Route path="/servers/:serverId/visits" element={<VisitListPage />} />
              <Route path="/servers/:serverId/visits/:visitId" element={<VisitDetailsPage />} />
              <Route path="/servers/:serverId/status-history" element={<ServerStatusHistoryPage />} />

              <Route path="/servers/:serverId/visit-alerts" element={<VisitAlertsPage />} />
              <Route path="/servers/:serverId/emergency-alerts" element={<EmergencyAlertsPage />} />
              <Route path="/servers/:serverId/notifications" element={<NotificationsPage />} />
              
              <Route path="/servers/:serverId/business-development" element={<BusinessDevelopmentPage />} />
              <Route path="/servers/:serverId/analytics/details" element={<AnalyticsDetailPage />} />
              
              <Route path="/users" element={<GlobalUsersPage />} />
              <Route path="/audit-logs" element={<AuditLogsPage />} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Layout>
        )
      } />
    </Routes>
  );
}

// This component handles routing for the support domain (support.architecture.care) and ensures it remains isolated from the main superadmin panel. It also includes a security redirect to prevent access to the old public portal on the superadmin domain.
const SupportDomainRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<PublicSupportPortal />} />
      <Route path="/track" element={<PublicTicketTracker />} />
      <Route path="/feedback" element={<PublicFeedbackPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const DomainSwitcher = () => {
  const hostname = window.location.hostname;
  
  // Matches support.architecture.care OR support.localhost
  const isSupportDomain = hostname.startsWith('support'); 

  if (isSupportDomain) {
    return <SupportDomainRoutes />;
  }

  // Security Redirect: If someone tries to access the old public portal on the superadmin domain
  if (window.location.pathname.startsWith('/support-portal')) {
    // Strip the old base path, but keep everything after it (e.g., /track)
    let cleanPath = window.location.pathname.replace('/support-portal', '');
    if (cleanPath === '') cleanPath = '/';
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // If testing locally, redirect to the local masked domain
        window.location.href = `http://support.localhost:${window.location.port}${cleanPath}${window.location.search}`;
    } else {
        // If in production, redirect to the live masked domain
        window.location.href = `https://support.architecture.care${cleanPath}${window.location.search}`;
    }
    return null;
  }

  // Otherwise, load normal routes for the superadmin panel
  return <AppRoutes />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DomainSwitcher />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;